import React, { useState, useEffect } from 'react';

interface Recommendation {
    name: string;
    description: string;
    rating: number;
    address: string;
}

interface RecommendationData {
    location: string;
    eat: Recommendation[];
    fun: Recommendation[];
    stay: Recommendation[];
}

interface RecommendationPopupProps { }

export const RecommendationPopup: React.FC<RecommendationPopupProps> = () => {
    const [data, setData] = useState<RecommendationData | null>(null);
    const [activeTab, setActiveTab] = useState<'eat' | 'fun' | 'stay'>('eat');
    const [isAutoPlay, setIsAutoPlay] = useState(true);
    const tabs = ['eat', 'fun', 'stay'] as const;
    const tabLabels: Record<typeof tabs[number], string> = {
        eat: 'Ăn uống',
        fun: 'Giải trí',
        stay: 'Lưu trú'
    };

    // Listen for custom events to show / close the recommendations popup
    useEffect(() => {
        const handleShowRecommendations = (event: CustomEvent<RecommendationData>) => {
            setData(event.detail);
        };
        const handleCloseRecommendations = () => {
            setData(null);
        };

        window.addEventListener('show_recommendations', handleShowRecommendations as EventListener);
        window.addEventListener('close_recommendations', handleCloseRecommendations as EventListener);

        return () => {
            window.removeEventListener('show_recommendations', handleShowRecommendations as EventListener);
            window.removeEventListener('close_recommendations', handleCloseRecommendations as EventListener);
        };
    }, []);

    // Reset state when popup opens (data changes from null to valid)
    useEffect(() => {
        if (data) {
            setActiveTab('eat');
            setIsAutoPlay(true);
        }
    }, [data]);

    const handleClose = () => {
        setData(null);
    };

    useEffect(() => {
        if (!isAutoPlay || !data) return;

        const timer = setInterval(() => {
            setActiveTab((current) => {
                const currentIndex = tabs.indexOf(current);
                const nextIndex = (currentIndex + 1) % tabs.length;
                return tabs[nextIndex];
            });
        }, 5000);

        return () => clearInterval(timer);
    }, [isAutoPlay, data]);

    if (!data) return null;

    const renderList = (items: Recommendation[]) => (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
            {items.map((item, index) => (
                <div key={index} className="bg-white/10 p-4 rounded-xl border border-white/20 hover:bg-white/15 transition-all">
                    <div className="flex justify-between items-start">
                        <h3 className="text-lg font-bold text-white">{item.name}</h3>
                        <div className="flex items-center bg-yellow-400/20 px-2 py-1 rounded text-yellow-300 text-xs font-bold">
                            ★ {item.rating}
                        </div>
                    </div>
                    <p className="text-gray-300 text-sm mt-1">{item.description}</p>
                    <div className="flex items-center gap-1 mt-3 text-xs text-gray-400">
                        <span className="w-3 h-3 block bg-white/20 rounded-full" />
                        {item.address}
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col animate-in fade-in zoom-in duration-300">
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-gradient-to-r from-blue-900/30 to-purple-900/30">
                <div>
                    <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                        Gợi ý địa điểm
                    </h2>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">tại {data.location}</p>
                </div>
                <button
                    onClick={handleClose}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/20 transition-colors text-gray-400 hover:text-white"
                >
                    ✕
                </button>
            </div>

            {/* Tabs */}
            <div className="flex flex-col bg-black/40 m-4 rounded-lg relative overflow-hidden">
                <div className="flex p-1 z-10">
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => {
                                setActiveTab(tab);
                                setIsAutoPlay(false);
                            }}
                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all capitalize ${activeTab === tab
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            {tabLabels[tab]}
                        </button>
                    ))}
                </div>
                {isAutoPlay && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-white/10">
                        <div
                            key={activeTab}
                            className="h-full bg-blue-400 origin-left"
                            style={{
                                animation: 'timeline 5s linear forwards'
                            }}
                        />
                    </div>
                )}
            </div>

            <style>{`
                @keyframes timeline {
                    from { width: 0%; }
                    to { width: 100%; }
                }
            `}</style>

            {/* Content */}
            <div className="p-4 pt-0 flex-1 min-h-[300px]">
                {activeTab === 'eat' && renderList(data.eat)}
                {activeTab === 'fun' && renderList(data.fun)}
                {activeTab === 'stay' && renderList(data.stay)}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-white/10 bg-black/20 text-center">
                <p className="text-[10px] text-gray-500">Powered by StarGlobal Location Services</p>
            </div>
        </div>
    );
};
